import { FC, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PrimaryButton, Button } from '@cb/apricot-react';
import { CircularProgress, Grid } from '@mui/material';

import { APP_URLS } from 'src/constants';
import { useAuth } from 'src/contexts';
import { Typography } from 'src/components';
import { ISummary, TAIForm, IAIUpdateRequest } from 'src/types';
import { buildCSV } from 'src/utils';
import { useToasts } from 'src/hooks';
import { flush, loadSummaries, getFile } from 'src/services/schoolRequest';
import { UpdateForm } from '../../components/school';
import SummarySearch from '../../components/admin/SummarySearch';
import { findOrgById } from 'src/apis';

let loading = false;
let progress: string = 'Retrieving Records';

const LoadProgress = (props: any) => {
  const [message, setMessage] = useState(props.message || '');

  useEffect(() => {
    setMessage(props.message);
  }, [props.message]);

  return (
    <div>
      {message &&
        <Grid item xs={12}>
          <CircularProgress /> {message}
        </Grid>
      }
    </div>
  );
};

const AdminHome: FC = () => {
  const { userName } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState<ISummary[]>([]);
  const { render: renderToasts, toastErrorMessage } = useToasts();
  const [savingCSV, setSavingCSV] = useState(false);
  const [viewing, setViewing] = useState<IAIUpdateRequest | undefined>();

  const handleViewRequest = useCallback(
    async (data: ISummary) => {
      try {
        if (!data.submission)
          throw new Error("Cannot retrieve a non-existent filePath.");

        if (data.appliedType === 'aia' && data.requestType === 'create') {
          navigate(APP_URLS.FORM, {
            state: {
              summary: data
            }
          });
        } else if (data.requestType === 'create') {
          const aiForm: TAIForm = await getFile(data.submission);
          navigate(APP_URLS.SCHOOL, {
            state: {
              school: {
                ...aiForm,
                materials: data.materials || [],
              },
              referrer: {
                status: ''
              }
            },
          });
        } else {
          const update: IAIUpdateRequest = await getFile(data.submission);
          if (data.orgId) {
            // @DEPRECATED - This block is only need to allow opening old submissions
            // that didn't include the orgTypeCd originally, otherwise we can get all data
            // from the submission record
            if (!update.data?.orgTypeCd) {
              const record = await findOrgById(data.orgId);
              update.data.orgTypeCd = record.org.orgTypeCd;
            }
          }
          setViewing(update);
        }

      } catch (error: any) {
        toastErrorMessage(error.message);
      }
    },
    [navigate],
  );

  const handleSaveAsCSV = useCallback(async () => {
    try {
      setSavingCSV(true);
      await buildCSV(requests);
    } catch (error: any) {
      toastErrorMessage(error.message);
    } finally {
      setSavingCSV(false);
    }
  }, [requests, toastErrorMessage]);

  const handleUpdateRequest = (request: ISummary) => {
    init();
  };

  const handleReload = () => {
    flush();
    init();
  };

  const handleRefresh = () => {
    init();
  };

  const init = () => {
    (async () => {
      if (loading) return;
      loading = true;
      progress = 'Loading...';
      setRequests([]);
      const records = await loadSummaries({});
      loading = false;
      progress = '';
      setRequests(records.all());
    })();
  };

  useEffect(() => {
    (async () => {
      init();
    })();
  }, []);

  return (<div>
    <Grid container spacing={2}>
      {viewing ?
        <Grid item xs={12}>
          <div style={{ marginBottom: '10px' }}>
            <Button onClick={() => setViewing(undefined)}>Close</Button>
          </div>
          <UpdateForm onCancel={() => setViewing(undefined)} readOnly={true} request={viewing} />
        </Grid> :
        <>
          {renderToasts()}
          <Grid item xs={12}>
            <Grid container justifyContent="space-between">
              <Typography variant="h4">Welcome, {userName}</Typography>

              <PrimaryButton disabled={savingCSV} onClick={handleSaveAsCSV}>
                Save As CSV
                {savingCSV && <CircularProgress size="small" />}
              </PrimaryButton>
            </Grid>
            <LoadProgress message={progress}></LoadProgress>
            {!progress &&
              <div>
                <button className='cb-btn cb-btn-primary' style={{ marginRight: '10px' }} onClick={handleRefresh}>Refresh</button>
                <button className='cb-btn cb-btn-primary' onClick={handleReload}>Hard Reload</button>
              </div>
            }
          </Grid>
          <Grid item xs={12}>
            <SummarySearch data={requests} onViewRequest={handleViewRequest} onUpdateRequest={handleUpdateRequest} />
          </Grid>
        </>
      }
    </Grid>
  </div>);
};

export default AdminHome;
